import { NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  FileSearch,
  Users,
  Ship,
  Package,
  UserCog,
  Settings as SettingsIcon,
  ChevronRight,
  Hammer,
  LogOut,
  Eye,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/quotes", label: "Quotes", icon: FileText, testid: "nav-quotes", permission: "quotes" },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products", permission: "products" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients", permission: "clients" },
  { to: "/vessels", label: "Vessels", icon: Ship, testid: "nav-vessels", permission: "vessels" },
  { to: "/tenders", label: "Tenders", icon: FileSearch, testid: "nav-tenders", permission: "tenders" },
  { to: "/projects", label: "Projects", icon: Hammer, testid: "nav-projects", permission: "projects" },
  { to: "/staff", label: "Staff", icon: UserCog, testid: "nav-staff", adminOnly: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", adminOnly: true },
];

// Returns the subset of NAV entries that the current user should see in
// the sidebar. Admin always gets everything; staff sees only the pages
// matching their granted permissions; admin-only entries (Staff, Settings)
// are visible to admins only.
function visibleNav(user) {
  return NAV.filter((n) => {
    if (n.adminOnly) return user?.role === "admin";
    if (user?.role === "admin") return true;
    if (!n.permission) return true;
    return (user?.permissions || []).includes(n.permission);
  });
}

export default function Layout() {
  const { user, logout, togglePreviewAsClient } = useAuth();
  const canPreviewAsClient = user?.role === "admin" || user?.role === "staff";

  return (
    <div className="flex h-screen w-full bg-[#F6F7F8] text-[#011836]">
      <aside
        className="hidden md:flex md:w-64 lg:w-72 flex-col border-r border-[#12335F] bg-[#011836]"
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-[#12335F]">
          <Logo />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {visibleNav(user).map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[#0f2735] text-[#66C2EA] border border-[#0088CC]/40"
                      : "text-[#8FA6C0] hover:text-white hover:bg-[#0A2A55] border border-transparent"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                <span className="font-display tracking-wide">{n.label}</span>
                <ChevronRight className="ml-auto h-3.5 w-3.5 opacity-0 group-hover:opacity-60 transition-opacity" />
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-[#12335F] px-4 py-4">
          {canPreviewAsClient && (
            <button
              type="button"
              onClick={() => togglePreviewAsClient(true)}
              className="w-full mb-3 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-xs font-display uppercase tracking-wider font-semibold"
              data-testid="sidebar-preview-as-client-btn"
              title="See the app exactly as a client would"
            >
              <Eye className="h-3.5 w-3.5" />
              Preview as client
            </button>
          )}
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate font-medium" data-testid="user-name">
                {user?.name || user?.email}
              </div>
              <div className="text-xs uppercase tracking-wider text-[#66C2EA]" data-testid="user-role">
                {user?.role}
              </div>
            </div>
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[10px] uppercase tracking-wider font-display font-semibold text-[#8FA6C0] hover:text-white hover:bg-[#0A2A55] border border-[#12335F] shrink-0"
              data-testid="sidebar-logout-btn"
              title="Sign out"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 flex items-center justify-between border-b border-[#12335F] bg-[#011836] px-4 py-3">
        <Logo className="h-7 w-auto" showWordmark={false} />
        <div className="flex items-center gap-3">
          <div className="text-xs uppercase tracking-wider text-[#66C2EA]">{user?.role}</div>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] uppercase tracking-wider font-display font-semibold text-[#8FA6C0] hover:text-white border border-[#12335F]"
            data-testid="mobile-logout-btn"
            aria-label="Sign out"
          >
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <main className="flex-1 overflow-y-auto pt-14 md:pt-0">
        <Outlet />
      </main>

      {/* Mobile nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-[#12335F] bg-[#011836] flex justify-around py-2">
        {visibleNav(user).slice(0, 5).map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`${n.testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-3 py-1 text-[10px] ${
                  isActive ? "text-[#66C2EA]" : "text-[#8FA6C0]"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
